import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Compass, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient, type ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { useTour } from '@/lib/tour/tour-provider';
import { ALL_TOURS, type TourDefinition } from '@/lib/tour/tour-registry';
import { cn } from '@/lib/utils';

function getErrorMessage(err: unknown): string {
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  if (err instanceof Error && err.message) return err.message;
  return 'Erro inesperado. Tente novamente.';
}

/**
 * Configurações > Tour do sistema.
 *
 * Lista todos os tours guiados com o status atual (concluído/pendente) e
 * permite rever qualquer um deles a qualquer momento, mesmo já concluído
 * ("Rever" navega até a rota do módulo e força o início via
 * `reviewTour` — não é o mesmo fluxo do indicador pulsante da sidebar,
 * que só dispara quando o tour ainda está pendente).
 *
 * "Reativar todos os indicadores" zera `completed`/`dismissedAll` no
 * backend (PATCH /me/onboarding com resetTours), fazendo os pontos
 * pulsantes voltarem pra todos os módulos na sidebar. O modal de
 * boas-vindas continua dispensado (dismissed permanece true) — reativar
 * indicadores não é a mesma coisa que reabrir o modal do primeiro acesso.
 */
export function SystemTourPage() {
  const navigate = useNavigate();
  const onboarding = useAuthStore((s) => s.onboarding);
  const setOnboarding = useAuthStore((s) => s.setOnboarding);
  const { reviewTour } = useTour();

  const resetMutation = useMutation({
    mutationFn: () => apiClient.meUpdateOnboarding({ resetTours: true }),
    onSuccess: (next) => setOnboarding(next),
  });

  // Pede o tour ANTES de navegar (mesma ordem do clique num item da
  // sidebar — ver NavLink em sidebar.tsx): reviewTour so grava a intencao
  // (pendingRef) e tenta ativar; se a rota alvo ainda nao bate, fica
  // pendente. O TourProvider persiste durante toda a navegacao dentro de
  // /escritorio (montado uma vez no DashboardShell — esta pagina, sim,
  // desmonta quando navigate() troca de rota, mas o estado pendente NAO
  // mora aqui, mora no provider), entao ele mesmo reavalia e ativa o tour
  // assim que location.pathname bater com a rota alvo. Um fix anterior
  // tentou adiar essa chamada pra DEPOIS da navegacao com um ref local
  // desta pagina — nao funcionava pra nenhum modulo: esta pagina desmonta
  // no instante em que a rota muda (React Router nunca da a ela uma
  // renderizacao com o pathname novo antes de desmontar — confirmado com
  // teste isolado), entao o effect que dependia de ver o pathname mudar
  // nunca disparava. A ambiguidade de prefixo que aquele fix tentava
  // resolver (Configuracoes: esta pagina mora em .../configuracoes/tour,
  // subrota da propria rota do tour de Configuracoes) agora e tratada
  // dentro do proprio TourProvider (tryStartPending), que compara o
  // pathname atual com o pathname de origem do pedido.
  function handleReview(tour: TourDefinition) {
    reviewTour(tour.key);
    navigate(tour.route);
  }

  function handleResetAll() {
    const confirmed = window.confirm(
      'Reativar os indicadores de todos os módulos? Os tours já concluídos voltam a aparecer como pendentes na barra lateral.',
    );
    if (!confirmed) return;
    resetMutation.mutate();
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
            Tour do sistema
          </h1>
          <p className="mt-1 text-sm text-[var(--fg-muted)]">
            Reveja o tour guiado de qualquer módulo quando quiser, mesmo que já tenha concluído.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleResetAll}
          disabled={resetMutation.isPending}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          {resetMutation.isPending ? 'Reativando...' : 'Reativar todos os indicadores'}
        </Button>
      </div>

      {resetMutation.isError && (
        <p className="text-sm text-red-600">{getErrorMessage(resetMutation.error)}</p>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
            <tr>
              <th className="px-4 py-2 font-medium">Módulo</th>
              <th className="w-36 px-4 py-2 font-medium">Status</th>
              <th className="w-28 px-4 py-2 text-right font-medium">Ação</th>
            </tr>
          </thead>
          <tbody>
            {ALL_TOURS.map((tour) => {
              const done = onboarding.completed.includes(tour.key);
              return (
                <tr
                  key={tour.key}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--fg)]">{tour.label}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
                        done
                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600'
                          : 'border-[var(--border)] bg-[var(--surface-muted)] text-[var(--fg-muted)]',
                      )}
                    >
                      {done ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <Circle className="h-3.5 w-3.5" />
                      )}
                      {done ? 'Concluído' : 'Pendente'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReview(tour)}
                    >
                      <Compass className="mr-1.5 h-3.5 w-3.5" />
                      Rever
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
